/**
 * Session cache for the idea picker's cards, keyed by language + theme chip +
 * idea length. Lives outside the component because step 1 unmounts when the
 * wizard moves to step 2 — coming back must not cost another ideas request.
 * Capped so the blob stays small; sessionStorage failures are ignored (the
 * cache is a bonus).
 */
const KEY = 'snugtale_picker_ideas_v2';
const MAX_ENTRIES = 24;

type Cache = Record<string, string[]>;

function read(): Cache {
  try {
    const raw = sessionStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Cache) : {};
  } catch {
    return {};
  }
}

function cacheKey(language: string, themeKey: string, length: string): string {
  return `${language}:${themeKey}:${length}`;
}

export function getCachedIdeas(language: string, themeKey: string, length: string): string[] | null {
  const ideas = read()[cacheKey(language, themeKey, length)];
  return Array.isArray(ideas) && ideas.length > 0 ? ideas : null;
}

export function setCachedIdeas(language: string, themeKey: string, length: string, ideas: string[]): void {
  try {
    const cache = read();
    const key = cacheKey(language, themeKey, length);
    delete cache[key];
    const entries = Object.entries(cache).slice(-(MAX_ENTRIES - 1));
    entries.push([key, ideas]);
    sessionStorage.setItem(KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    /* ignore */
  }
}
